/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: TODO: fix */
/** biome-ignore-all lint/suspicious/noExplicitAny: TODO: fix */

import * as v from 'valibot';

import type { FormatRef, JArray, JBoolean, JNumber, JObject, JProperty, JSchema, JString } from '../types';

/** Matches keys like x-min-<anything>-for-status-<StatusName> (e.g. x-min-collaborators-for-status-Internal Review) */
const MIN_ITEMS_FOR_STATUS_KEY_PATTERN = /^x-min-.+-for-status-(.+)$/;

type Validators = [v.BaseSchema<any, any, any>, ...v.BaseValidation<any, any, any>[]];

const messages = {
  integer: (label?: string) => (s: v.BaseIssue<any>) => `${label || s.path} can only be an integer`,
  maxLength:
    ({ label, maxLength }: { label?: string; maxLength: number }) =>
    (s: v.BaseIssue<any>) =>
      `${label || s.path} must be at most ${maxLength} characters`,
  maxValue:
    ({ label, max }: { label?: string; max: number }) =>
    (s: v.BaseIssue<any>) =>
      `${label || s.path} must be at most ${max}`,
  minLength:
    ({ label, minLength }: { label?: string; minLength: number }) =>
    (s: v.BaseIssue<any>) =>
      `${label || s.path} must be at least ${minLength} characters`,

  minValue:
    ({ label, min }: { label?: string; min: number }) =>
    (s: v.BaseIssue<any>) =>
      `${label || s.path} must be at least ${min}`,

  required: (label?: string) => (s: v.BaseIssue<any>) => `${label || s.path} is required`,
} as const;

const unwrapOption = (value: unknown): unknown =>
  value && typeof value === 'object' && 'id' in value ? (value as { id: unknown }).id : value;

const getFormats = (schema: JString): string[] => {
  const rawFormats = schema.format === 'x-composite-format' ? (schema as any)['x-composite-formats'] : schema.format;

  if (typeof rawFormats === 'string') {
    return rawFormats
      .split(';')
      .map((f) => f.trim())
      .filter(Boolean);
  }

  return [];
};

export function parseMinItemsForStatusFromSchema(schema: Record<string, unknown>): Record<string, number> | null {
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(schema)) {
    const match = key.match(MIN_ITEMS_FOR_STATUS_KEY_PATTERN);
    const statusName = match?.[1];
    if (statusName !== undefined && typeof value === 'number') {
      result[statusName] = value;
    }
  }
  return Object.keys(result).length > 0 ? result : null;
}

export const parseRefPath = (schema: JString) => {
  const ref = schema['x-referenced-jsonpath-property'];
  if (!ref) {
    return null;
  }
  return ref
    .replace(/^\$\.\.?/, '')
    .split('.')
    .map((p) => p.charAt(0).toLowerCase() + p.slice(1))
    .join('.');
};

const STATUS_FORMAT_KEY = 'x-date-today-or-future-by-quote-status-format';
const DELTA_FORMAT_KEY = 'x-date-today-or-future-by-delta-format';

type DeltaConfig = {
  dateOnly: boolean;
  skipScenarios: ReadonlySet<string>;
};

const isRelationFormat = (format: string): format is FormatRef => {
  return /^x-ref-/.test(format) || format === STATUS_FORMAT_KEY || format === DELTA_FORMAT_KEY;
};

const parseStatusConfig = (schema: JString): Record<string, boolean> | null => {
  const prefix = `${STATUS_FORMAT_KEY}-`;
  const config: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(schema as unknown as Record<string, unknown>)) {
    if (key.startsWith(prefix) && typeof value === 'boolean') {
      config[key.slice(prefix.length)] = value;
    }
  }

  return Object.keys(config).length > 0 ? config : null;
};

const parseDeltaConfig = (schema: JString): DeltaConfig => {
  const prefix = `${DELTA_FORMAT_KEY}-`;
  const schemaRecord = schema as unknown as Record<string, unknown>;

  const rawScenarios = schemaRecord[`${prefix}with-skip-scenario`];

  const skipScenarios = new Set<string>();

  if (Array.isArray(rawScenarios)) {
    for (const scenario of rawScenarios) {
      if (typeof scenario !== 'string') {
        continue;
      }

      const [oldStatus, newStatus, ...rest] = scenario.split(':').map((s) => s.trim());

      if (rest.length || !oldStatus || !newStatus) {
        continue;
      }

      skipScenarios.add(`${oldStatus}|${newStatus}`);
    }
  }

  return {
    dateOnly: schemaRecord[`${prefix}x-date-only`] === true,
    skipScenarios,
  };
};

const isJObjectSchema = (schema: JSchema): schema is JObject => {
  return schema.type === 'object';
};

const parseLabel = (schema: JSchema, path: string | null) => {
  if (path && isJObjectSchema(schema)) {
    const property = schema.properties[path];
    if (property && 'label' in property) {
      return property.label || null;
    }
  }
  return null;
};

const getAllLabels = (fieldLabel: string | undefined, requiredAnyFields: string[], rootSchema: JSchema) => {
  const labels = requiredAnyFields.map((value) => parseLabel(rootSchema, value) || '');
  if (fieldLabel) {
    labels.unshift(fieldLabel);
  }

  return labels;
};

const getDateOnlyString = (dateStr: string) => {
  const parts = dateStr.split(' ');

  return `${parts[2]}-${parts[1]}-${parts[3]}`;
};

const isTodayOrFuture = (value: string, dateOnly: boolean) => {
  const inputDate = new Date(dateOnly ? getDateOnlyString(value) : value);

  const today = new Date();

  if (dateOnly) {
    today.setHours(0, 0, 0, 0);
  }

  return inputDate >= today;
};

const createStringSchema = (schema: JString, rootSchema: JSchema) => {
  const isNullable = Array.isArray(schema.type) && schema.type.includes('null');
  const refPath = parseRefPath(schema);
  const requiredAnyFields = schema['x-required-any-fields'] || [];
  const formats = getFormats(schema);
  const baseValidator = v.config(
    v.pipe(
      v.unknown(),
      v.transform((rawInput) => {
        const input = unwrapOption(rawInput);

        if (typeof input === 'string') {
          return input;
        }
        // If input is array, join to string
        if (Array.isArray(input)) {
          return input.join(',');
        }
        // Temporary handling old forms that could send date objects
        // TODO: remove after migrating to new forms
        if (
          input &&
          typeof input === 'object' &&
          'toLocaleString' in input &&
          typeof input.toLocaleString === 'function'
        ) {
          return input.toLocaleString();
        }
        if (input && typeof input === 'number' && typeof input.toString === 'function') {
          return input.toString();
        }
        return '';
      }),
      v.string(),
      v.metadata({
        deltaConfig: parseDeltaConfig(schema),
        format: schema.format,
        formats,
        label: schema.label,
        refLabel: parseLabel(rootSchema, refPath),
        refPath, // => $..shipDate -> shipData
        requiredAnyFields,
        requiredAnyFieldsLabels: getAllLabels(schema.label, requiredAnyFields, rootSchema),
        statusConfig: parseStatusConfig(schema),
      }),
    ),
    { abortPipeEarly: true },
  );

  const validators: Validators = [
    isNullable
      ? baseValidator
      : v.config(v.pipe(baseValidator, v.nonEmpty(messages.required(schema.label))), { abortPipeEarly: true }),
  ];

  if (schema.minLength !== undefined) {
    validators.push(
      v.minLength(schema.minLength, messages.minLength({ label: schema.label, minLength: schema.minLength })),
    );
  }
  if (schema.maxLength !== undefined) {
    validators.push(
      v.maxLength(schema.maxLength, messages.maxLength({ label: schema.label, maxLength: schema.maxLength })),
    );
  }

  const dateTodayOrFutureFormatRule = v.custom<string>((val) => {
    const dateOnly = schema['x-date-only'];
    const inputDate = new Date(dateOnly ? getDateOnlyString(val as string) : (val as string));
    const today = new Date();
    if (dateOnly) {
      today.setHours(0, 0, 0, 0);
    }

    return inputDate >= today;
  }, `${schema.label} must be greater or equal to today's date`);

  // Add format validation (supports composite formats)
  for (const format of formats) {
    if (!format || isRelationFormat(format)) {
      continue;
    }

    switch (format) {
      case 'email':
        validators.push(v.email('Invalid email format'));
        break;
      case 'uri':
        validators.push(v.url('Invalid URL format'));
        break;
      case 'guid':
      case 'uuid':
        validators.push(v.uuid('Invalid UUID format'));
        break;
      case 'date-time':
        validators.push(v.isoTimestamp('Invalid date-time'));
        break;
      case 'date':
        validators.push(v.isoDate('Invalid date'));
        break;
      case 'time':
        validators.push(v.isoTime('Invalid time'));
        break;
      case 'x-date-today-or-future-format':
        validators.push(v.pipe(v.string(), dateTodayOrFutureFormatRule));
        break;

      default:
        console.error(`Unsupported format: ${format}`);
    }
  }

  return v.config(v.pipe(...validators), { abortPipeEarly: true });
};

const createNumberSchema = (schema: JNumber) => {
  const isInteger = schema.type === 'integer';
  const validators: Validators = [
    v.config(
      v.pipe(
        v.unknown(),
        v.transform((rawValue) => {
          // string → number
          const value = unwrapOption(rawValue);

          if (!value) {
            return undefined;
          }
          const numberValue = Number(value);
          return Number.isNaN(numberValue) ? undefined : numberValue;
        }),
        v.number(messages.required(schema.label)),
        v.metadata({
          label: schema.label,
        }),
      ),
      { abortPipeEarly: true },
    ),
  ];
  if (isInteger) {
    validators.push(v.integer(messages.integer(schema.label)));
  }
  if (schema.minimum !== undefined) {
    validators.push(v.minValue(schema.minimum, messages.minValue({ label: schema.label, min: schema.minimum })));
  }
  if (schema.maximum !== undefined) {
    validators.push(v.maxValue(schema.maximum, messages.maxValue({ label: schema.label, max: schema.maximum })));
  }

  return v.config(v.pipe(...validators), { abortPipeEarly: true });
};

const getSchemaByRef = (ref: `#/definitions/${string}`, definitions?: Record<string, JProperty>): JProperty => {
  const [, , schemaName] = ref.split('/');
  if (!definitions || !schemaName || !definitions[schemaName]) {
    throw new Error('[validator]: failed to parse jSchema by definition');
  }
  return definitions[schemaName];
};

const createArraySchema = (schema: JArray, rootSchema: JSchema, definitions?: Record<string, JProperty>) => {
  // biome-ignore lint/complexity/useLiteralKeys: suppress
  const itemSchema = '$ref' in schema.items ? getSchemaByRef(schema.items['$ref'], definitions) : schema.items;

  const validators: Validators = [v.array(createValibotSchema(itemSchema, rootSchema))];
  // Add length constraints
  if (schema.minItems !== undefined) {
    validators.push(v.minLength(schema.minItems));
  }
  if (schema.maxItems !== undefined) {
    validators.push(v.maxLength(schema.maxItems));
  }

  return v.config(v.pipe(...validators), { abortPipeEarly: true });
};

const createObjectSchema = (schema: JObject, definitions: Record<string, JProperty> = {}) => {
  // BE can send empty schema without properties field for the object type definition schema
  if (!schema.properties) {
    return v.object({});
  }

  const properties: Record<string, v.BaseSchema<any, any, any>> = {};

  // Convert each property
  for (const [key, propSchema] of Object.entries(schema.properties)) {
    properties[key] = createValibotSchema(propSchema, schema, definitions);
  }

  // Handle required fields
  // For required fields, use the schema as-is
  // For optional fields, wrap with v.optional()
  const objectProperties: Record<string, v.BaseSchema<any, any, any>> = {};

  for (const [key, propSchema] of Object.entries(properties)) {
    if (schema.required?.includes(key)) {
      objectProperties[key] = propSchema;
    } else {
      objectProperties[key] = v.nullish(propSchema);
    }
  }

  const valibotSchema = v.object(objectProperties);

  const relations = createRelations(valibotSchema);

  if (!relations.length) {
    return valibotSchema;
  }

  // @ts-expect-error TODO: fixed type
  return v.config(v.pipe(valibotSchema, ...relations.map(createCustomRefSchema).filter(Boolean)), {
    abortPipeEarly: false,
  });
};

const formatRefValidators: Record<
  FormatRef,
  (params: {
    path: string;
    refPath: string;
    pathLabel: string;
    refPathLabel: string | null;
    requiredAnyFields: string[];
    requiredAnyFieldsLabels: string[];
    statusConfig?: Record<string, boolean> | null;
    deltaConfig?: DeltaConfig | null;
  }) => {
    validator: (input: Record<string, unknown>) => boolean;
    message: (s: v.BaseIssue<any>) => string;
  }
> = {
  [DELTA_FORMAT_KEY]: ({ path, pathLabel, deltaConfig }) => {
    const dateOnly = deltaConfig?.dateOnly ?? false;
    const skipScenarios = deltaConfig?.skipScenarios;

    return {
      message: () => `${pathLabel} must be greater or equal to today's date`,

      validator: (input: Record<string, unknown>) => {
        const currentStatus = input.status as string | undefined;

        if (!currentStatus) {
          return true;
        }

        const shouldSkip =
          skipScenarios?.has(`${currentStatus}|${currentStatus}`) || skipScenarios?.has(`All|${currentStatus}`);

        if (shouldSkip) {
          return true;
        }

        const val = input[path];

        if (!val) {
          return true;
        }

        if (typeof val !== 'string') {
          throw Error('Date parse issue');
        }

        return isTodayOrFuture(val, dateOnly);
      },
    };
  },
  [STATUS_FORMAT_KEY]: ({ path, pathLabel, statusConfig }) => {
    const dateOnly = statusConfig?.['x-date-only'] ?? false;

    return {
      message: () => `${pathLabel} must be greater or equal to today's date`,
      validator: (input: Record<string, unknown>) => {
        const status = input.status as string | undefined;

        // TODO: logical mistake on BE side, status properties in statusConfig should be false to prevent validation,
        // but now it's true when validation should be prevented, change after BE fix
        if (!status || !statusConfig || statusConfig[status]) {
          return true;
        }

        const val = input[path];
        if (!val) {
          return true;
        }

        if (typeof val !== 'string') {
          throw Error('Date parse issue');
        }

        const inputDate = new Date(dateOnly ? getDateOnlyString(val) : val);
        const today = new Date();

        if (dateOnly) {
          today.setHours(0, 0, 0, 0);
        }

        return inputDate >= today;
      },
    };
  },
  'x-ref-format-date-equal-or-more': ({ path, refPath, pathLabel, refPathLabel }) => {
    return {
      message: (_s: v.BaseIssue<any>) => {
        return `${pathLabel} should be the same as or later than ${refPathLabel}`;
      },
      validator: (input) => {
        if (typeof input[path] === 'string' && typeof input[refPath] === 'string') {
          return new Date(input[path]) >= new Date(input[refPath]);
        }
        return false;
      },
    };
  },
  'x-ref-format-date-less': ({ path, refPath, pathLabel, refPathLabel }) => {
    return {
      message: () => `${pathLabel} Date must be before ${refPathLabel}`,
      validator: (input: Record<string, unknown>) => {
        const val = input[path];
        const refVal = input[refPath];

        if (!val || !refVal) {
          return true;
        }

        if (typeof val !== 'string' || typeof refVal !== 'string') {
          throw Error('Date parse issue');
        }

        const fieldDate = new Date(getDateOnlyString(val));
        const refDate = new Date(getDateOnlyString(refVal));

        return fieldDate < refDate;
      },
    };
  },
  'x-ref-one-of-required': ({ path, requiredAnyFields, requiredAnyFieldsLabels }) => {
    const fields = [path, ...requiredAnyFields];

    const isFilled = (v: unknown) =>
      v !== null &&
      v !== undefined &&
      !(typeof v === 'string' && v.trim() === '') &&
      !(Array.isArray(v) && v.length === 0);

    return {
      message: (_s: v.BaseIssue<any>) => `At least one of ${requiredAnyFieldsLabels.join(', ')} must be provided`,
      validator: (input: Record<string, unknown>) => fields.some((f) => isFilled(input[f])),
    };
  },
};

const createCustomRefSchema = ({
  path,
  refPath,
  pathLabel,
  refPathLabel,
  format,
  requiredAnyFields,
  requiredAnyFieldsLabels,
  statusConfig,
  deltaConfig,
}: {
  path: string;
  refPath: string;
  pathLabel: string;
  refPathLabel: string;
  format: FormatRef;
  requiredAnyFields: string[];
  requiredAnyFieldsLabels: string[];
  statusConfig?: Record<string, boolean> | null;
  deltaConfig?: DeltaConfig | null;
}) => {
  // switch format
  const validator = formatRefValidators[format]?.({
    deltaConfig,
    path,
    pathLabel,
    refPath,
    refPathLabel,
    requiredAnyFields,
    requiredAnyFieldsLabels,
    statusConfig,
  });
  if (!validator) {
    return;
  }

  const isStatusDependentFormat = format === STATUS_FORMAT_KEY || format === DELTA_FORMAT_KEY;

  return v.forward(
    v.partialCheck(
      isStatusDependentFormat ? ([[path], ['status']] as const) : ([[path], [refPath]] as const),
      validator.validator,
      validator.message,
    ),
    [path],
  );
};

const isOptional = (s: v.BaseSchema<any, any, any>): s is v.OptionalSchema<any, any> =>
  s.type === 'optional' || s.type === 'nullish';

const unwrapOptional = (schema: v.BaseSchema<any, any, any>) => {
  let current = schema;
  while (isOptional(current)) {
    current = current.wrapped;
  }
  return current;
};

export const createRelations = (schema: v.ObjectSchema<v.ObjectEntries, undefined>) => {
  const relations: {
    path: string;
    refPath: string;
    pathLabel: string;
    refPathLabel: string | null;
    format: FormatRef;
    requiredAnyFields?: string[];
    requiredAnyFieldsLabels: string[];
    statusConfig?: Record<string, boolean> | null;
    deltaConfig?: DeltaConfig | null;
  }[] = [];

  for (const entry in schema.entries) {
    if (!schema.entries[entry]) {
      continue;
    }

    const entrySchema = unwrapOptional(schema.entries[entry]);
    const meta = v.getMetadata(entrySchema) as {
      format: string;
      formats?: string[];
      label: string;
      refPath: string | null;
      refLabel: string | null;
      requiredAnyFields?: string[];
      requiredAnyFieldsLabels: string[];
      statusConfig?: Record<string, boolean> | null;
      deltaConfig?: DeltaConfig | null;
    };

    const relationFormats = (meta.formats ?? [meta.format]).filter(isRelationFormat);

    if (!relationFormats.length) {
      continue;
    }

    for (const format of relationFormats) {
      relations.push({
        deltaConfig: meta.deltaConfig,
        format,
        path: entry,
        pathLabel: meta.label,
        refPath: meta.refPath || '',
        refPathLabel: meta.refLabel,
        requiredAnyFields: meta.requiredAnyFields,
        requiredAnyFieldsLabels: meta.requiredAnyFieldsLabels,
        statusConfig: meta.statusConfig,
      });
    }
  }

  return relations;
};

const createBooleanSchema = (schema: JBoolean) => {
  return v.pipe(
    v.boolean(),
    v.metadata({
      label: schema.label,
    }),
  );
};

// Convert your JSON schema to Valibot schema
export function createValibotSchema(
  schema: JSchema,
  rootSchema?: JSchema,
  baseDefinitions: Record<string, JProperty> = {},
): v.BaseSchema<any, any, any> {
  const type = Array.isArray(schema.type) ? schema.type.find((t) => t !== 'null') : schema.type;
  const definitions = 'definitions' in schema ? schema.definitions : {};
  const latestDefinitions = { ...baseDefinitions, ...definitions };

  switch (type) {
    case 'string':
      return createStringSchema(schema as JString, rootSchema ?? schema);

    case 'number':
    case 'integer':
      return createNumberSchema(schema as JNumber);

    case 'boolean':
      return createBooleanSchema(schema as JBoolean);

    case 'array':
      return createArraySchema(schema as JArray, rootSchema ?? schema, latestDefinitions);

    case 'object':
      return createObjectSchema(schema as JObject, latestDefinitions);

    default:
      throw new Error(`Unsupported schema type: ${(schema as any).type}`);
  }
}

export function parseValidationIssues<T extends v.InferIssue<v.BaseSchema<any, any, any>>[]>(issues: T) {
  const errorMap: Record<string, any> = {};
  const messages: string[] = [];

  // Process each issue/error
  issues.forEach((issue) => {
    const path: undefined | { key: string | number; type: 'object' | 'array' }[] = issue.path;
    const message = issue.message;

    if (path && path.length > 0) {
      // Build the nested path from the path array
      let current: any = errorMap;

      // Navigate through the path, creating nested structures as needed
      for (let i = 0; i < path.length; i++) {
        const segment = path[i];

        if (segment) {
          const key = segment.key;
          const type = segment.type;

          // If this is the last segment, add the error message
          if (i === path.length - 1) {
            // For the final segment, always add to an array of messages
            if (!current[key]) {
              current[key] = [];
            }
            if (Array.isArray(current[key])) {
              current[key].push(message);
            }
          } else {
            // Determine what type of structure to create based on the next segment
            const nextSegment = path[i + 1];

            if (!current[key]) {
              if (nextSegment?.type === 'array') {
                // Next level is an array, so create an array
                current[key] = [];
              } else {
                // Next level is an object, so create an object
                current[key] = {};
              }
            }

            // Handle array indices - ensure array is long enough and has proper structure
            if (type === 'array' && typeof key === 'number') {
              // Ensure the array is long enough
              while (current.length <= key) {
                current.push(undefined);
              }

              // Initialize the array element if it's undefined
              if (current[key] === undefined) {
                if (nextSegment?.type === 'array') {
                  current[key] = [];
                } else {
                  current[key] = {};
                }
              }
            }

            // Move to the next level
            current = current[key];
          }
        }
      }
    } else {
      messages.push(message);
    }
  });

  return messages.length ? messages : errorMap;
}

export function getFieldSchema(schema: v.BaseSchema<any, any, any>, path: string[] | string) {
  let current = schema;

  const normalizedPath = typeof path === 'string' ? normalizePath(path).split('.') : path;

  for (const key of normalizedPath) {
    if (current.type === 'array') {
      // For arrays, we want the item schema regardless of the specific index
      current = (current as v.ArraySchema<any, any>).item;

      // Skip the numeric index key since we've already moved to the item schema
      if (!Number.isNaN(Number(key))) {
        continue;
      }
    }

    const entries = (current as unknown as v.ObjectSchema<any, any>).entries;
    if (!entries || !(key in entries)) {
      return null;
    }

    let next = entries[key];
    if (isOptional(next)) {
      next = next.wrapped; // unwrap optional
    }
    current = next;
  }

  return current;
}

export function normalizePath(path: string): string {
  // Helper function to normalize paths that might contain array indices
  return path
    .replace(/\[(\d+)\]/g, '.$1') // Convert [0] to .0
    .replace(/^\./, ''); // Remove leading dot
}

export function hasField(schema: v.BaseSchema<any, any, any>, path: string) {
  return getFieldSchema(schema, path) !== null;
}

export function isFieldRequired(schema: v.BaseSchema<any, any, any>, path: string) {
  const keys = normalizePath(path).split('.');
  let current = schema;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i] as string;
    const isLastKey = i === keys.length - 1;

    // Handle array access
    if (current.type === 'array') {
      current = (current as unknown as v.ArraySchema<any, any>).item;

      // Skip numeric index for arrays
      if (!Number.isNaN(Number(key))) {
        continue;
      }
    }

    const entries = (current as unknown as v.ObjectSchema<any, any>).entries;
    if (!entries || !(key in entries)) {
      return false;
    }
    const next = entries[key];
    current = next;

    if (isOptional(next) && !isLastKey) {
      current = next.wrapped; // unwrap optional
    }
  }
  if (isOptional(current)) {
    return false;
  }

  return true;
}
